import React from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import {Chip, Tooltip} from "@mui/material";

import {blue50, blue500, green50, green600, greyishBlue600, orange50, orange600, red50, red600} from "assets/jss/main";

import {formatDate} from "./formatters";

const CATEGORY_COLORS = {
    wellbeing: {backgroundColor: red50, color: red600},
    delivery: {backgroundColor: red50, color: red600},
    failure: {backgroundColor: orange50, color: orange600},
    complaint: {backgroundColor: orange50, color: orange600},
    question: {backgroundColor: blue50, color: blue500},
    privacy: {backgroundColor: blue50, color: blue500},
    abuse: {backgroundColor: red50, color: red600},
};

HolderChip.propTypes = {
    handledBy: PropTypes.string,
    memberEmail: PropTypes.string,
};

export function HolderChip({handledBy, memberEmail}) {
    if (!handledBy) {
        return (
            <Chip size="small" label={<FormattedMessage id="personalShopper.holder.assistant" />} sx={{backgroundColor: green50, color: green600, fontWeight: 600}} />
        );
    }

    return (
        <Tooltip title={handledBy}>
            <Chip
                size="small"
                label={handledBy === memberEmail ? <FormattedMessage id="personalShopper.holder.me" /> : handledBy}
                sx={{backgroundColor: orange50, color: orange600, fontWeight: 600, maxWidth: "180px"}}
            />
        </Tooltip>
    );
}

EscalationChips.propTypes = {
    categories: PropTypes.arrayOf(PropTypes.string),
};

export function EscalationChips({categories = []}) {
    if (categories.length === 0) {
        return null;
    }

    return (
        <span style={{display: "inline-flex", gap: "4px", flexWrap: "wrap"}}>
            {categories.map((category) => (
                <Chip
                    key={category}
                    size="small"
                    label={<FormattedMessage id={`personalShopper.escalation.${category}`} />}
                    sx={{...(CATEGORY_COLORS[category] ?? CATEGORY_COLORS.question), fontWeight: 600}}
                />
            ))}
        </span>
    );
}

ExpiryChip.propTypes = {
    retention: PropTypes.shape({
        expiresAt: PropTypes.string,
        expiringSoon: PropTypes.bool,
    }),
};

/**
 * The sheet is near the end of its retention: Sylius erases it on that date unless the
 * shopper writes again. Shown only inside the warning window.
 */
export function ExpiryChip({retention}) {
    if (!retention?.expiringSoon) {
        return null;
    }

    return (
        <Tooltip title={<FormattedMessage id="personalShopper.retention.expiringSoon.tooltip" />}>
            <Chip
                size="small"
                label={<FormattedMessage id="personalShopper.retention.expiresAt" values={{date: formatDate(retention.expiresAt)}} />}
                sx={{backgroundColor: orange50, color: orange600, fontWeight: 600}}
            />
        </Tooltip>
    );
}

MarketingOptOutChip.propTypes = {
    optedOut: PropTypes.bool,
};

export function MarketingOptOutChip({optedOut}) {
    if (!optedOut) {
        return null;
    }

    return (
        <Tooltip title={<FormattedMessage id="personalShopper.retention.optOut.tooltip" />}>
            <Chip size="small" variant="outlined" label={<FormattedMessage id="personalShopper.retention.optOut" />} sx={{color: greyishBlue600, borderColor: greyishBlue600}} />
        </Tooltip>
    );
}

SuspendedChip.propTypes = {
    suspendedAt: PropTypes.string,
    suspensionReason: PropTypes.string,
};

/**
 * The access was suspended for abusive content: the assistant no longer answers that number
 * until the team lifts it from the conversation page.
 */
export function SuspendedChip({suspendedAt, suspensionReason}) {
    if (!suspendedAt) {
        return null;
    }

    return (
        <Tooltip title={suspensionReason || ""}>
            <Chip size="small" label={<FormattedMessage id="personalShopper.suspension.chip" />} sx={{backgroundColor: red50, color: red600, fontWeight: 600}} />
        </Tooltip>
    );
}

ErasureRequestedChip.propTypes = {
    erasureRequestedAt: PropTypes.string,
};

/**
 * The shopper asked in the chat for their data to be erased: they confirm with the keyword
 * within 24 hours, or the team completes it by hand once the request is confirmed otherwise.
 */
export function ErasureRequestedChip({erasureRequestedAt}) {
    if (!erasureRequestedAt) {
        return null;
    }

    return (
        <Tooltip title={<FormattedMessage id="personalShopper.privacy.erasureRequested" values={{date: formatDate(erasureRequestedAt)}} />}>
            <Chip size="small" label={<FormattedMessage id="personalShopper.erasure.chip" />} sx={{backgroundColor: red50, color: red600, fontWeight: 600}} />
        </Tooltip>
    );
}

WindowChip.propTypes = {
    open: PropTypes.bool,
};

export function WindowChip({open}) {
    return (
        <Chip
            size="small"
            variant="outlined"
            label={<FormattedMessage id={open ? "personalShopper.window.open" : "personalShopper.window.closed"} />}
            sx={{color: open ? green600 : red600, borderColor: open ? green600 : red600}}
        />
    );
}
